<template>
  <div class="pagination-container">
    <div class="result">{{ $t(`Result`) }}</div>
    <div class="items-per-page">
      <div class="filter-dropdown" ref="dropdownRef" @click="toggleDropdown">
        <div>
          <div class="filter-value">{{ perPage }}</div>
        </div>
        <img
          src="@/assets/img/arrow-down.svg"
          class="arrow-down"
          :class="{ rotated: dropdownVisible }"
          alt="arrow"
        />
        <div v-if="dropdownVisible" class="dropdown-menu" @click.stop>
          <div
            v-for="option in itemsPerPageOptions"
            :key="option"
            @click="changeItemsPerPage(option)"
            class="dropdown-item"
          >
            {{ option }}
          </div>
        </div>
      </div>
    </div>

    <div class="pagination-controls">
      <button class="arrow-button" @click="prevPage" :disabled="isOnFirstPage">
        <svg
          width="32"
          height="32"
          viewBox="0 0 32 32"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g opacity="0.6">
            <path
              d="M29.3332 16C29.3332 23.3638 23.3636 29.3333 15.9998 29.3333C8.63604 29.3333 2.6665 23.3638 2.6665 16C2.6665 8.63616 8.63604 2.66663 15.9998 2.66663C23.3636 2.66663 29.3332 8.63616 29.3332 16Z"
              stroke="white"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M15.9998 21.3333L10.6665 16L15.9998 10.6666"
              stroke="white"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M21.3332 16L10.6665 16"
              stroke="white"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </g>
        </svg>
      </button>

      <div class="page-numbers">
        <span
          @click="goToPage(1)"
          :class="{ active: currentPage === 1 }"
          class="page-number"
          >1</span
        >
        <span v-if="startPage > 2" class="dots">...</span>
        <span
          v-for="page in displayedPages"
          :key="page"
          @click="goToPage(page)"
          :class="{ active: currentPage === page }"
          class="page-number"
        >
          {{ page }}
        </span>
        <span v-if="endPage < totalPages - 1" class="dots">...</span>
        <span
          v-if="totalPages > 1"
          @click="goToPage(totalPages)"
          :class="{ active: currentPage === totalPages }"
          class="page-number"
        >
          {{ totalPages }}
        </span>
      </div>

      <button class="arrow-button" @click="nextPage" :disabled="isOnLastPage">
        <svg
          width="32"
          height="32"
          viewBox="0 0 32 32"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g opacity="0.6">
            <path
              d="M2.66683 16C2.66683 23.3638 8.63637 29.3333 16.0002 29.3333C23.364 29.3333 29.3335 23.3638 29.3335 16C29.3335 8.63616 23.364 2.66663 16.0002 2.66663C8.63637 2.66663 2.66683 8.63616 2.66683 16Z"
              stroke="white"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M16.0002 21.3333L21.3335 16L16.0002 10.6666"
              stroke="white"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M10.6668 16L21.3335 16"
              stroke="white"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </g>
        </svg>
      </button>
    </div>
  </div>
</template>

<script>
import { onClickOutside } from "@vueuse/core";

export default {
  props: {
    totalPages: {
      type: Number,
      required: true,
    },
    perPage: {
      type: Number,
      default: 20,
    },
    currentPage: {
      type: Number,
      default: 1,
    },
  },
  emits: ["update:perPage", "update:currentPage"],
  data() {
    return {
      dropdownVisible: false,
      itemsPerPageOptions: [20, 40, 60],
    };
  },
  computed: {
    startPage() {
      if (this.currentPage <= 3) return 2;
      if (this.currentPage >= this.totalPages - 2) return this.totalPages - 3;
      return this.currentPage - 1;
    },
    endPage() {
      if (this.currentPage <= 3) return Math.min(5, this.totalPages - 1);
      if (this.currentPage >= this.totalPages - 2) return this.totalPages - 1;
      return this.currentPage + 1;
    },
    displayedPages() {
      const pages = [];
      for (let i = this.startPage; i <= this.endPage; i++) {
        if (i !== 1 && i !== this.totalPages) {
          pages.push(i);
        }
      }
      return pages;
    },
    isOnFirstPage() {
      return this.currentPage === 1;
    },
    isOnLastPage() {
      return this.currentPage === this.totalPages;
    },
  },
  mounted() {
    onClickOutside(this.$refs.dropdownRef, () => {
      this.dropdownVisible = false;
    });
  },
  methods: {
    toggleDropdown() {
      this.dropdownVisible = !this.dropdownVisible;
    },
    changeItemsPerPage(newPerPage) {
      this.$emit("update:perPage", newPerPage);
      this.dropdownVisible = false;
    },
    goToPage(page) {
      if (page !== this.currentPage) {
        this.$emit("update:currentPage", page);
      }
    },
    prevPage() {
      if (!this.isOnFirstPage) {
        this.goToPage(this.currentPage - 1);
      }
    },
    nextPage() {
      if (!this.isOnLastPage) {
        this.goToPage(this.currentPage + 1);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.pagination-container {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-left: auto;
  flex-wrap: wrap;
  margin-top: 16px;
}
.items-per-page {
  display: none;
}
.result {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  text-align: left;
  color: white;
  margin-right: 7px;
  display: none;
}

.items-per-page {
  margin-right: 16px;
}

.filter-dropdown {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 6px 6px 6px 12px;
  border-radius: 6px;
  border: 1px solid #44445a;
  background: #191919;
  cursor: pointer;
  position: relative;
  max-width: 72px;
  width: 100%;
  display: none;
  /* height: 32px; */
}

// .filter-label {
//     font-size: 12px;
//     font-weight: 400;
//     line-height: 16px;
//     text-align: left;
//     color: #a8b2bd;
// }

.filter-value {
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  color: white;
}

.arrow-down {
  transition: all 0.3s ease;
}

.arrow-down.rotated {
  transform: rotate(180deg);
}

.dropdown-menu {
  position: absolute;
  top: 40px;
  left: 0;
  background-color: #191919;
  color: white;
  padding: 5px;
  border: 1px solid #44445a;
  border-radius: 6px;
  width: 100%;
  z-index: 10;
}

.dropdown-item {
  padding: 6px 6px 6px 12px;
  border-radius: 8px;
  cursor: pointer;
}

.dropdown-item:hover {
  background-color: #11131c;
}

.pagination-controls {
  display: flex;
  align-items: center;
}

.arrow-button {
  background: none;
  border: none;
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.arrow-button:disabled {
  cursor: not-allowed;

  svg path {
    stroke-opacity: 0.2;
  }
}

.page-numbers {
  display: flex;
  align-items: center;
  margin: 0 8px;
  padding: 6px 8px;
  border-radius: 6px;
}

.page-number {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;
  font-weight: 700;
  line-height: 19px;
  color: white;

  &:not(:first-child, :last-child) {
    margin: 0 2px;
  }
}

.page-number.active {
  background: #9d1616;
  border-radius: 4px;
}

.dots {
  color: white;
  cursor: default;
  margin: 0 2px;
}
</style>
